/**
 * Login screen (screen #1 in UI_APPROACH.md §4). Not one of the 12 kit
 * components — it is a screen composed from Fluent primitives, like the search
 * input in Find. Delegates to the auth store, which calls the platform
 * `/auth/login` and keeps the returned JWT in memory only.
 */
import { useState, type FormEvent } from 'react';
import {
  Button,
  Field,
  Input,
  Spinner,
  Title2,
  Body1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAuthStore } from '../state/auth';

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    width: '340px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXXL,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  error: { color: tokens.colorPaletteRedForeground1 },
});

export interface LoginPageProps {
  /** Optional post-login callback (the app router uses store status by default). */
  onAuthenticated?: () => void;
}

export function LoginPage({ onAuthenticated }: LoginPageProps) {
  const styles = useStyles();
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);
  const login = useAuthStore((s) => s.login);

  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const busy = status === 'authenticating';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await login(user, password);
      onAuthenticated?.();
    } catch {
      /* error surfaced via store */
    }
  }

  return (
    <main className={styles.root}>
      <section className={styles.card} aria-labelledby="login-title">
        <div className={styles.header}>
          <Title2 as="h1" id="login-title">
            Multimedia Automation Platform
          </Title2>
          <Body1>Sign in with your Dynizer account.</Body1>
        </div>
        <form className={styles.form} onSubmit={onSubmit} aria-describedby={error ? 'login-error' : undefined}>
          <Field label="Username" required>
            <Input
              name="user"
              value={user}
              autoComplete="username"
              disabled={busy}
              onChange={(_e, d) => setUser(d.value)}
            />
          </Field>
          <Field label="Password" required>
            <Input
              name="password"
              type="password"
              value={password}
              autoComplete="current-password"
              disabled={busy}
              onChange={(_e, d) => setPassword(d.value)}
            />
          </Field>
          {error ? (
            <Body1 id="login-error" role="alert" className={styles.error}>
              {error}
            </Body1>
          ) : null}
          <Button type="submit" appearance="primary" disabled={busy || !user || !password}>
            {busy ? <Spinner size="tiny" label="Signing in…" /> : 'Sign in'}
          </Button>
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
